/** Theme preference — SPEC §2.2 dark/light, §6.5 design tokens, BRAND.md palette.
 * Light is the default (2026-10-20, same as the phone app). Dark and System ("follow this
 * device") are settings, persisted locally, never uploaded (privacy by default).
 */

/** What is painted: always one of the two palettes. */
export type ThemeMode = "dark" | "light";
/** What the person picked. "system" follows prefers-color-scheme live. */
export type ThemePref = ThemeMode | "system";

const KEY = "veto.theme";
/** Broadcast on every repaint with the RESOLVED mode (dark | light), so the top-bar toggle
 *  and Settings never drift. Read `getThemePref()` for the choice itself. */
export const THEME_EVENT = "veto:theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function systemMode(): ThemeMode {
  try {
    return typeof window !== "undefined" && window.matchMedia?.(DARK_QUERY).matches
      ? "dark"
      : "light";
  } catch {
    return "light";
  }
}

/** The saved choice; light when nothing (or something unknown) is saved. */
export function getThemePref(): ThemePref {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "light" || saved === "dark" || saved === "system") return saved;
  } catch {
    /* private mode */
  }
  // Light is the default (owner's choice, same as the phone app); Dark stays one tap away.
  return "light";
}

export function resolveTheme(pref: ThemePref = getThemePref()): ThemeMode {
  return pref === "system" ? systemMode() : pref;
}

/** The palette to paint now (the saved choice, with "system" resolved). */
export function getPreferredTheme(): ThemeMode {
  return resolveTheme(getThemePref());
}

/** Paint a palette. Does not change the saved choice. */
function paint(mode: ThemeMode): void {
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
  // Both theme-color metas (one per media query in index.html) follow the painted palette,
  // otherwise a dark OS would keep a dark status bar over the light app.
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute("content", mode === "light" ? "#f2f4f9" : "#05070c");
  });
}

function broadcast(mode: ThemeMode): void {
  window.dispatchEvent(new CustomEvent<ThemeMode>(THEME_EVENT, { detail: mode }));
}

let systemQuery: MediaQueryList | null = null;
const onSystemChange = () => {
  if (getThemePref() !== "system") return;
  const mode = systemMode();
  paint(mode);
  broadcast(mode);
};

/** Listen to the OS scheme while "system" is the choice; stop otherwise. */
function syncSystemListener(pref: ThemePref): void {
  if (typeof window === "undefined" || !window.matchMedia) return;
  if (pref === "system") {
    if (systemQuery) return;
    try {
      systemQuery = window.matchMedia(DARK_QUERY);
      systemQuery.addEventListener?.("change", onSystemChange);
    } catch {
      systemQuery = null;
    }
  } else if (systemQuery) {
    systemQuery.removeEventListener?.("change", onSystemChange);
    systemQuery = null;
  }
}

/** Save + paint (legacy name kept for callers that pass a resolved mode). */
export function applyTheme(pref: ThemePref): ThemeMode {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* ignore */
  }
  const mode = resolveTheme(pref);
  paint(mode);
  syncSystemListener(pref);
  return mode;
}

/** Save, paint and notify subscribers so independent controls never drift. */
export function setTheme(pref: ThemePref): void {
  broadcast(applyTheme(pref));
}

/** Startup: paint the saved choice (without rewriting storage) and follow the OS if asked. */
export function initTheme(): ThemeMode {
  const pref = getThemePref();
  const mode = resolveTheme(pref);
  paint(mode);
  syncSystemListener(pref);
  return mode;
}
